import React from 'react';
import { ChevronDown } from 'lucide-react';
import type { FacultyMember, Status } from '../../types/faculty';
import { formatFacultyStatus } from '../../types/faculty';

interface FacultyProfileHeaderProps {
  faculty: FacultyMember;
  onEdit: () => void;
  onContact: () => void;
}

const statusTone = (status: Status) => {
  if (status === 'active') return { dot: 'bg-aquamarine', text: 'text-jungle' };
  if (status.startsWith('on_leave')) return { dot: 'bg-coral', text: 'text-iron-grey' };
  if (status === 'inactive' || status === 'terminated') {
    return { dot: 'bg-text-tertiary', text: 'text-text-secondary' };
  }
  return { dot: 'bg-primary', text: 'text-iron-grey' };
};

export function FacultyProfileHeader({ faculty, onEdit, onContact }: FacultyProfileHeaderProps) {
  const statusStyle = statusTone(faculty.status);

  return (
    <div className="mb-6 flex flex-col items-start justify-between gap-6 rounded-xl border border-border bg-white p-6 shadow-sm md:flex-row md:items-center">
      <div className="flex items-center gap-6">
        <div className="h-20 w-20 flex-shrink-0">
          {faculty.profilePhoto ? (
            <img
              className="h-20 w-20 rounded-full border-2 border-border object-cover"
              src={faculty.profilePhoto}
              alt=""
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-border bg-muted font-heading text-2xl font-semibold text-text-secondary">
              {faculty.firstName[0]}
              {faculty.lastName[0]}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1 flex items-center gap-3">
            <h1 data-testid="faculty-name" className="font-heading text-2xl font-semibold text-iron-grey">
              {faculty.firstName} {faculty.lastName}
            </h1>
            <div className="flex items-center gap-1.5 rounded-full border border-border bg-background-light px-2.5 py-1">
              <div className={`h-2 w-2 rounded-full ${statusStyle.dot}`} />
              <span data-testid="status-badge" className={`text-xs font-medium ${statusStyle.text}`}>
                {formatFacultyStatus(faculty.status)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1 text-sm text-text-secondary sm:flex-row sm:items-center sm:gap-4">
            {[faculty.hebrewFirstName, faculty.hebrewLastName].some(Boolean) ? (
              <span data-testid="hebrew-name" dir="rtl" className="font-body">
                {[faculty.hebrewFirstName, faculty.hebrewLastName].filter(Boolean).join(' ')}
              </span>
            ) : null}
            {[faculty.hebrewFirstName, faculty.hebrewLastName].some(Boolean) ? (
              <span className="hidden text-border-strong sm:inline">•</span>
            ) : null}
            <span data-testid="employee-id" className="font-title text-xs uppercase tracking-wider">
              {faculty.employeeId}
            </span>
            <span className="hidden text-border-strong sm:inline">•</span>
            <span>{faculty.position}</span>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center gap-3 md:w-auto">
        <button
          type="button"
          data-testid="edit-profile-btn"
          onClick={onEdit}
          className="inline-flex flex-1 items-center justify-center rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-iron-grey shadow-sm transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 md:flex-none"
        >
          Edit profile
        </button>
        <div className="relative flex-1 md:flex-none">
          <button
            type="button"
            data-testid="contact-btn"
            onClick={onContact}
            className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Contact
            <ChevronDown className="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
